import { SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';
import cls from 'classnames';
import { useAtomValue } from 'jotai';
import { Colors } from '@lemon8/design';
import { countryData } from './country-list';
import { useClickAway } from './use-click-away';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import DownFill from '@lemon8/design/lib/icons/icon_assets/arrow_triangle_down_fill.svg';

import './style.scss';

const target = 'https://lemon8-law-enforcement.zendesk.com/hc/en-us/requests/new';

const countryList = countryData.map((d) => d.name);

export const Form = (): JSX.Element => {
  const { isMobile, isInApp } = useAtomValue($UserAgent);

  const [focus, setFocus] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredCountryList, setFilterCountryList] = useState(countryList);
  const inputRef = useRef<HTMLDivElement>(null);

  const handleInputValueChange = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setInputValue(value);
    setFilterCountryList(countryList.filter((c) => c.toLowerCase().includes(value.toLowerCase())));
  }, []);

  const handleFocus = useCallback(() => {
    setFocus(true);
  }, []);

  const disabled = useMemo(() => {
    return !countryList.includes(inputValue);
  }, [inputValue]);

  const handleBlue = useCallback(() => {
    setFocus(false);
  }, []);

  useClickAway(inputRef, handleBlue);

  const handleConfirm = useCallback(() => {
    window.open(target, '_blank');
  }, []);

  return (
    <div
      className={cls('document-container', {
        'platform-inapp': isInApp,
        'platform-mobile': isMobile,
        'has-nav': !isInApp,
      })}
    >
      <h1>Lemon8 Law Enforcement request form</h1>
      <p>
        This form is for sworn law enforcement officials only. Any requests submitted by non-sworn law enforcement
        agencies will not be responded to.
      </p>
      <br />
      <div className="law-enforcement-guideline-form">
        <p>
          <strong>*Agency country or region</strong>
        </p>
        <p style={{ color: Colors.N100_60 }}>
          Select your agency&apos;s country or region to be directed to the corresponding form.
        </p>
        <div className="input" ref={inputRef}>
          <input
            value={inputValue}
            onFocus={handleFocus}
            placeholder={'Select country or region'}
            onChange={handleInputValueChange}
          />
          <DownFill />
          <div className="option-list" style={{ visibility: focus ? 'visible' : 'hidden' }}>
            <div className="left-arrow" />
            <div className="option-list-inner">
              {filteredCountryList.map((c) => (
                <div
                  className="option-item"
                  key={c}
                  onClickCapture={() => {
                    setInputValue(c);
                    setFocus(false);
                    setFilterCountryList(countryList);
                  }}
                >
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={cls('submit', { disabled })} onClick={handleConfirm}>
          Submit
        </div>
        <p style={{ color: Colors.N100_40 }}>
          Click “Submit”, then you will be redirected to a 3rd-party website to continue filling out the form.
        </p>
      </div>
    </div>
  );
};
